import React, { forwardRef, MouseEvent, useCallback } from 'react'
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton } from 'react-share'
import useKeypress from '@hooks/useKeyPress'
import { useTranslation } from 'react-i18next'

import facebookIcon from '@assets/images/icons/share/facebook.png'
import facebookIconHover from '@assets/images/icons/share/facebook-hover.png'

import linkedinIcon from '@assets/images/icons/share/linkedin.png'
import linkedinIconHover from '@assets/images/icons/share/linkedin-hover.png'

import xIcon from '@assets/images/icons/share/x.png'
import xIconHover from '@assets/images/icons/share/x-hover.png'

import moreIcon from '@assets/images/icons/share/more.png'
import moreIconHover from '@assets/images/icons/share/more-hover.png'

import copyIcon from '@assets/images/icons/copy.png'

import closeIcon from '@assets/images/nav/close.png'
import closeHoverIcon from '@assets/images/nav/close-hover.png'

import './ShareModal.scss'
import { useShare } from '@hooks/useShare'
import { CSSTransition } from 'react-transition-group'

export const ShareModal = () => {
  const { t } = useTranslation('ShareModal')
  const { shareModalOpened, handleCloseShareModal, datagotchiImg, datagotchiImgFile, url, isLoading } = useShare()
  const nodeRef = React.useRef(null)

  useKeypress('Escape', handleCloseShareModal)

  const moreOnClick = () => {
    if (!navigator.canShare) {
      if (!datagotchiImg) {
        return
      }

      const link = document.createElement('a')
      link.href = datagotchiImg
      link.download = 'datagotchi.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      if (!datagotchiImgFile) {
        return
      }

      navigator.share({
        title: t('native.title'),
        text: t('native.text'),
        files: [datagotchiImgFile],
      })
    }
  }

  const handleHeaderClose = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    handleCloseShareModal()
  }, [])

  const copyToClipboard = () => {
    if (!url) {
      return
    }
    navigator.clipboard.writeText(url)
  }

  if (!shareModalOpened) {
    return null
  }

  return (
    <CSSTransition
      in={shareModalOpened}
      nodeRef={nodeRef}
      timeout={0}
      classNames=""
      mountOnEnter
      unmountOnExit>
      <div className="ShareModal">
        <div className="close">
          <button className="close-button" onClick={handleHeaderClose}>
            <ImageWithHover src={closeIcon} srcHover={closeHoverIcon} alt={t('close')} />
          </button>
        </div>
        {isLoading ? (
          <div className="ShareModal-loader">
            <div id="loading-gotchi" />
          </div>
        ) : (
          <>
            <div className="shareWith">{t('shareWith')}</div>
            <div className="ShareModal-share-buttons">
              <div className="ShareModal-share-buttons-button">
                <FacebookShareButton hashtag="Datagotchi" url={url || ''}>
                  <ImageWithHover src={facebookIcon} srcHover={facebookIconHover} alt={t('facebook')} />
                </FacebookShareButton>
                <label>{t('facebook')}</label>
              </div>

              <div className="ShareModal-share-buttons-button">
                <TwitterShareButton title={t('native.text')} hashtags={['Datagotchi']} url={url || ''}>
                  <ImageWithHover src={xIcon} srcHover={xIconHover} alt={t('x')} />
                </TwitterShareButton>
                <label>{t('x')}</label>
              </div>
              <div className="ShareModal-share-buttons-button">
                <LinkedinShareButton title={t('native.text')} url={url || ''}>
                  <ImageWithHover src={linkedinIcon} srcHover={linkedinIconHover} alt={t('linkedin')} />
                </LinkedinShareButton>
                <label>{t('linkedin')}</label>
              </div>
              <div className="ShareModal-share-buttons-button">
                <button onClick={moreOnClick}>
                  <ImageWithHover src={moreIcon} srcHover={moreIconHover} alt={t('more')} />
                </button>
                <label>{t('more')}</label>
              </div>
            </div>
            <div className="img-copy">
              <div className="share-url-container">
                <div className="url">{url}</div>
              </div>
              <button className="copy-url-button" onClick={copyToClipboard}>
                {t('copy')}
                <img src={copyIcon} />
              </button>
            </div>
          </>
        )}
      </div>
    </CSSTransition>
  )
}

function ImageWithHover({ src, srcHover, alt }: { src: string; srcHover: string; alt: string }) {
  const handleImageMouseEnterOrLeave = (e: MouseEvent, image: string) => {
    ;(e.target as HTMLImageElement).src = image
  }

  return (
    <img
      className="ShareModal-Img"
      src={src}
      alt={alt}
      onMouseOver={(e) => handleImageMouseEnterOrLeave(e, srcHover)}
      onMouseLeave={(e) => handleImageMouseEnterOrLeave(e, src)}
    />
  )
}

export default forwardRef(ShareModal)
